import DeleteIcon from '@mui/icons-material/Delete';
import DraftsIcon from '@mui/icons-material/Drafts';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import NotificationsIcon from '@mui/icons-material/Notifications';
import {
  Badge,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AxiosConfig from '../../../config/AxiosConfig';
import { resetNotificationCount } from '../../states/login/loginSlice';
import EventTypeIcon from '../utils/common/EventTypeIcon.jsx';

const formatNotificationDate = (date) => {
  const notificationDate = new Date(date);
  const now = new Date();
  const diffMs = now - notificationDate;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return "À l'instant";
  }
  if (diffMinutes < 60) {
    return `Il y a ${diffMinutes} min`;
  }
  if (diffHours < 24) {
    return `Il y a ${diffHours} h`;
  }
  if (diffDays === 1) {
    return 'Hier';
  }
  return `Il y a ${diffDays} j`;
};

const formatDate = (date) => {
  if (!date) {
    return '';
  }
  const notificationDate = new Date(date);
  const today = new Date();
  const isToday = notificationDate.toDateString() === today.toDateString();

  if (isToday) {
    return notificationDate.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  } else {
    return notificationDate.toLocaleString('fr-FR', {
      month: 'long',
      day: 'numeric',
    });
  }
};

const NotificationBell = ({ profile }) => {
  const dispatch = useDispatch();
  const notificationCount = useSelector(
    (state) => state?.login?.notificationCount
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [tabIndex, setTabIndex] = useState(0); // 0 = Toutes, 1 = Non lues
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      const fetchNotifications = async () => {
        try {
          const res = await AxiosConfig.create('get', '/notifications/')
            .call()
            .getResponseData();
          setNotifications(res || []);
          dispatch(resetNotificationCount());
        } catch (error) {
          console.error(
            'Erreur lors de la récupération des notifications :',
            error
          );
        }
      };
      fetchNotifications();
    }
  }, [open, dispatch]);

  const filteredNotifications =
    tabIndex === 1
      ? notifications.filter((notif) => !notif.readAt)
      : notifications;

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleNavigate = async (notification) => {
    try {
      await AxiosConfig.create(
        'put',
        `/notifications/${notification._id}/read`
      ).call();
      setNotifications((prev) =>
        prev.map((notif) =>
          notif._id === notification._id
            ? { ...notif, readAt: new Date() }
            : notif
        )
      );
    } catch (error) {
      console.error('Erreur lors de la lecture de la notification :', error);
    }
    navigate(
      `/project/${notification?.ticket?.project?._id}/tickets/${
        notification?.ticket?._id
      }${notification?.task?._id ? `/tasks/${notification?.task?._id}` : ``}`
    );
    handleClose();
  };

  const handleArchive = async (notificationId, event) => {
    event.stopPropagation();
    try {
      await AxiosConfig.create(
        'put',
        `/notifications/${notificationId}/archive`
      ).call();
      setNotifications((prev) =>
        prev.filter((notif) => notif._id !== notificationId)
      );
    } catch (error) {
      console.error("Erreur lors de l'archivage de la notification :", error);
    }
  };

  return profile ? (
    <>
      <IconButton
        color="inherit"
        onClick={handleOpen}
        aria-label="Ouvrir les notifications"
      >
        <Badge badgeContent={notificationCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        autoFocus={false}
      >
        <Tabs
          value={tabIndex}
          onChange={(event, newValue) => setTabIndex(newValue)}
          variant="fullWidth"
        >
          <Tab
            label={
              <Stack direction="row" alignItems="center" spacing={2}>
                <span>Toutes</span>
                {notifications.length > 0 && (
                  <Badge
                    badgeContent={notifications.length}
                    color="primary"
                    sx={{ transform: 'scale(0.8)', transformOrigin: 'center' }}
                  />
                )}
              </Stack>
            }
          />
          <Tab
            label={
              <Stack direction="row" alignItems="center" spacing={2}>
                <span>Non lues</span>
                {notifications.filter((notif) => !notif.readAt).length > 0 && (
                  <Badge
                    badgeContent={
                      notifications.filter((notif) => !notif.readAt).length
                    }
                    color="error"
                    sx={{ transform: 'scale(0.8)', transformOrigin: 'center' }}
                  />
                )}
              </Stack>
            }
          />
        </Tabs>

        <Divider />

        {filteredNotifications.length === 0 ? (
          <MenuItem disabled>Aucune notification</MenuItem>
        ) : (
          filteredNotifications.map((notification) => (
            <MenuItem
              key={notification._id}
              onClick={() => handleNavigate(notification)}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                backgroundColor: notification.readAt
                  ? 'rgba(0, 0, 0, 0.1)'
                  : 'inherit',
                opacity: notification.readAt ? 0.6 : 1,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s ease-in-out',
                },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                width="100%"
              >
                <EventTypeIcon eventType={notification.eventType} />
                <Box flexGrow={1}>
                  <Typography variant="body2">
                    {`[${notification?.ticket?.reference}] ${
                      notification?.eventType?.name
                    }${
                      notification?.task
                        ? ` : ${notification?.task?.action?.name}${
                            notification?.eventType?.code === 'SCHEDULED_TASK'
                              ? ` à ${formatDate(
                                  notification?.task?.scheduledAt
                                )}`
                              : ''
                          }`
                        : ``
                    }`}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', marginTop: '4px' }}
                  >
                    {formatNotificationDate(notification.createdAt)}
                  </Typography>
                </Box>
                {notification.readAt ? (
                  <DraftsIcon fontSize="small" color="action" />
                ) : (
                  <MarkEmailUnreadIcon fontSize="small" color="action" />
                )}
                <IconButton
                  size="small"
                  onClick={(e) => handleArchive(notification._id, e)}
                  aria-label="Supprimer la notification"
                >
                  <DeleteIcon fontSize="small" color="error" />
                </IconButton>
              </Stack>
            </MenuItem>
          ))
        )}
        <Divider />
        <MenuItem
          onClick={() => navigate('/notifications')}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            color: 'primary.main',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              borderRadius: '4px',
              transition: 'background-color 0.2s ease-in-out',
            },
          }}
        >
          Voir toutes les notifications
        </MenuItem>
      </Menu>
    </>
  ) : null;
};

export default NotificationBell;
